import { isAr, byId, copyText } from './extra-common.js';
import { trackEvent } from '../core/analytics.js';

// Month names datasets
const AR_MONTHS_HIJRI = [
  'محرم', 'صفر', 'ربيع الأول', 'ربيع الآخر', 'جمادى الأولى', 'جمادى الآخرة',
  'رجب', 'شعبان', 'رمضان', 'شوال', 'ذو القعدة', 'ذو الحجة'
];

const EN_MONTHS_HIJRI = [
  'Muharram', 'Safar', 'Rabi al-Awwal', 'Rabi al-Thani', 'Jumada al-Ula', 'Jumada al-Akhira',
  'Rajab', 'Shaban', 'Ramadan', 'Shawwal', 'Dhu al-Qadah', 'Dhu al-Hijjah'
];

const AR_MONTHS_GREGORIAN = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
];

const EN_MONTHS_GREGORIAN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const AR_WEEKDAYS = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
const EN_WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Islamic Month Special Metadata Notes
const HIJRI_MONTH_NOTES_AR = {
  1: 'شهر محرّم • من الأشهر الحُرُم المباركة وبداية السنة الهجرية (يوم عاشوراء).',
  2: 'شهر صفَر • الشهر الثاني في التقويم القمري الإسلامي.',
  3: 'شهر ربيع الأول • شهر ذكرى المولد النبوي الشريف وهجرة المصطفى ﷺ.',
  4: 'شهر ربيع الآخر (الثاني) • الشهر الرابع في التقويم الهجري.',
  5: 'شهر جمادى الأولى • الشهر الخامس في التقويم الهجري.',
  6: 'شهر جمادى الآخرة • الشهر السادس الذي يسبق الأشهر المباركة.',
  7: 'شهر رجب • من الأشهر الحُرُم العظيمة (ذكرى الإسراء والمعراج).',
  8: 'شهر شعبان • شهر رفع الأعمال إلى الله تعالى والاستعداد لرمضان.',
  9: 'شهر رمضان المبارك • شهر الصيام والقرآن وليلة القدر المباركة.',
  10: 'شهر شوّال • عيد الفطر المبارك وفضل صيام الست من شوّال.',
  11: 'شهر ذو القعدة • من الأشهر الحُرُم والاستعداد لموسم الحج الأكبر.',
  12: 'شهر ذو الحجة • من الأشهر الحُرُم • العشر الأوائل ويوم عرفة وعيد الأضحى المبارك.'
};

const HIJRI_MONTH_NOTES_EN = {
  1: 'Muharram • One of the four sacred months and the beginning of the Hijri year (Ashura).',
  2: 'Safar • The second month of the Islamic lunar calendar.',
  3: 'Rabi al-Awwal • The month commemorating the blessed Mawlid and Hijra of the Prophet ﷺ.',
  4: 'Rabi al-Thani • The fourth month of the Islamic calendar.',
  5: 'Jumada al-Ula • The fifth month of the Islamic calendar.',
  6: 'Jumada al-Akhira • The sixth month of the Islamic calendar.',
  7: 'Rajab • One of the sacred months (commemorating Isra and Miraj).',
  8: 'Shaban • The month preceding Ramadan, known for the elevation of deeds.',
  9: 'Holy Ramadan • The blessed month of fasting, Qur’an, and Laylat al-Qadr.',
  10: 'Shawwal • Eid al-Fitr and the recommended six voluntary days of fasting.',
  11: 'Dhu al-Qadah • One of the sacred months and beginning of Hajj pilgrimage travel.',
  12: 'Dhu al-Hijjah • Sacred month • The first ten blessed days, Day of Arafah & Eid al-Adha.'
};

// DOM Elements
const gDayInput = byId('g-day');
const gMonthSelect = byId('g-month');
const gYearInput = byId('g-year');
const gregorianFullTextEl = byId('gregorian-full-text');
const copyGregBtn = byId('copy-greg-btn');

const hDayInput = byId('h-day');
const hMonthSelect = byId('h-month');
const hYearInput = byId('h-year');
const hijriFullTextEl = byId('hijri-full-text');
const copyHijriBtn = byId('copy-hijri-btn');
const hijriMetaDescEl = byId('hijri-meta-desc');

const weekdayDisplayEl = byId('conv-weekday-display');
const btnSetToday = byId('btn-set-today');
const btnCopyBoth = byId('btn-copy-both');

// Calendar Popover Elements (matching age-calculator)
const dateTrigger = byId('gregorian-date-trigger');
const triggerValue = byId('gregorian-trigger-value');
const calendarPopover = byId('gregorian-calendar-popover');
const calSelectMonth = byId('cal-select-month');
const calSelectYear = byId('cal-select-year');
const calPrevBtn = byId('cal-prev-btn');
const calNextBtn = byId('cal-next-btn');
const calTodayBtn = byId('cal-today-btn');
const calThisMonthBtn = byId('cal-this-month-btn');
const calWeekdays = byId('cal-weekdays');
const calDays = byId('cal-days');

// State flags
let isSyncing = false;
let calendarView = new Date();
let isInitialLoad = true;
let completedDebounceTimer = null;

// Formatters
const ummAlQuraFmt = new Intl.DateTimeFormat('en-US-u-ca-islamic-umalqura', {
  year: 'numeric', month: 'numeric', day: 'numeric', timeZone: 'UTC'
});

const monthFormatter = new Intl.DateTimeFormat(isAr ? 'ar-SA' : 'en-US', { month: 'long' });
const weekdayFormatter = new Intl.DateTimeFormat(isAr ? 'ar-SA' : 'en-US', { weekday: 'narrow' });
const fullWeekdayFormatter = new Intl.DateTimeFormat(isAr ? 'ar-SA' : 'en-US', { weekday: 'long' });

// 1. Core Conversion Functions
function getHijriParts(date) {
  const parts = ummAlQuraFmt.formatToParts(date);
  const year = parseInt(parts.find((p) => p.type === 'year')?.value || '1448', 10);
  const month = parseInt(parts.find((p) => p.type === 'month')?.value || '1', 10);
  const day = parseInt(parts.find((p) => p.type === 'day')?.value || '1', 10);
  return { year, month, day };
}

function convertHijriToGregorian(hYear, hMonth, hDay) {
  const approxGYear = Math.round(hYear * 0.97 + 622);
  const start = new Date(Date.UTC(approxGYear - 1, 0, 1));
  const end = new Date(Date.UTC(approxGYear + 1, 11, 31));

  let low = Math.floor(start.getTime() / 86400000);
  let high = Math.floor(end.getTime() / 86400000);
  let bestMatch = null;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const d = new Date(mid * 86400000);
    const parts = ummAlQuraFmt.formatToParts(d);
    const y = parseInt(parts.find((p) => p.type === 'year')?.value || '0', 10);
    const m = parseInt(parts.find((p) => p.type === 'month')?.value || '0', 10);
    const day = parseInt(parts.find((p) => p.type === 'day')?.value || '0', 10);

    if (y === hYear && m === hMonth && day === hDay) {
      return d;
    }
    if (y === hYear && m === hMonth) {
      bestMatch = d;
    }

    if (y < hYear || (y === hYear && m < hMonth) || (y === hYear && m === hMonth && day < hDay)) {
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return bestMatch || new Date(Date.UTC(approxGYear, hMonth - 1, hDay));
}

// 2. Formatters for Result Displays
function formatFullGregorian(d, m, y, weekdayName) {
  const mName = isAr ? AR_MONTHS_GREGORIAN[m - 1] : EN_MONTHS_GREGORIAN[m - 1];
  return isAr
    ? `${weekdayName}، ${d} ${mName} ${y} م`
    : `${weekdayName}, ${mName} ${d}, ${y} AD`;
}

function formatFullHijri(hDay, hMonth, hYear, weekdayName) {
  const mName = isAr ? AR_MONTHS_HIJRI[hMonth - 1] : EN_MONTHS_HIJRI[hMonth - 1];
  return isAr
    ? `${weekdayName}، ${hDay} ${mName} ${hYear} هـ`
    : `${weekdayName}, ${hDay} ${mName} ${hYear} AH`;
}

function getWeekdayName(date) {
  const dayIndex = date.getUTCDay();
  return isAr ? AR_WEEKDAYS[dayIndex] : EN_WEEKDAYS[dayIndex];
}

// 3. Two-Way Sync Handlers
function onGregorianChanged() {
  if (isSyncing) return;
  isSyncing = true;

  try {
    let y = parseInt(gYearInput?.value, 10);
    let m = parseInt(gMonthSelect?.value, 10);
    let d = parseInt(gDayInput?.value, 10);

    if (isNaN(y) || y < 1900) y = 2026;
    if (isNaN(m) || m < 1 || m > 12) m = 1;
    const maxDays = new Date(y, m, 0).getDate();
    if (gDayInput) gDayInput.max = maxDays;
    if (isNaN(d) || d < 1) d = 1;
    if (d > maxDays) d = maxDays;
    if (gDayInput) gDayInput.value = d;

    const gDate = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
    const hParts = getHijriParts(gDate);

    // Update Hijri Inputs
    if (hYearInput) hYearInput.value = hParts.year;
    if (hMonthSelect) hMonthSelect.value = hParts.month;
    if (hDayInput) {
      hDayInput.value = hParts.day;
    }

    // Update Display Texts
    const weekday = getWeekdayName(gDate);
    if (weekdayDisplayEl) weekdayDisplayEl.textContent = weekday;

    const gregFormatted = formatFullGregorian(d, m, y, weekday);
    const hijriFormatted = formatFullHijri(hParts.day, hParts.month, hParts.year, weekday);

    if (gregorianFullTextEl) gregorianFullTextEl.textContent = gregFormatted;
    if (hijriFullTextEl) hijriFullTextEl.textContent = hijriFormatted;
    if (triggerValue) {
      const mName = isAr ? AR_MONTHS_GREGORIAN[m - 1] : EN_MONTHS_GREGORIAN[m - 1];
      triggerValue.textContent = `${d} ${mName} ${y}`;
    }

    // Update Hijri Meta note
    if (hijriMetaDescEl) {
      const notes = isAr ? HIJRI_MONTH_NOTES_AR : HIJRI_MONTH_NOTES_EN;
      hijriMetaDescEl.textContent = notes[hParts.month] || (isAr ? 'تقويم أم القرى الرسمي المعتمد' : 'Official Umm al-Qura Calendar');
    }

    // Keep calendar view in sync
    calendarView = new Date(y, m - 1, 1);
  } finally {
    isSyncing = false;
  }
}

function onHijriChanged() {
  if (isSyncing) return;
  isSyncing = true;

  try {
    let hY = parseInt(hYearInput?.value, 10);
    let hM = parseInt(hMonthSelect?.value, 10);
    let hD = parseInt(hDayInput?.value, 10);

    if (isNaN(hY) || hY < 1340) hY = 1448;
    if (isNaN(hM) || hM < 1 || hM > 12) hM = 1;
    if (isNaN(hD) || hD < 1) hD = 1;
    if (hD > 30) hD = 30;
    if (hDayInput) hDayInput.value = hD;

    const gDate = convertHijriToGregorian(hY, hM, hD);
    const gY = gDate.getUTCFullYear();
    const gM = gDate.getUTCMonth() + 1;
    const gD = gDate.getUTCDate();

    // Update Gregorian inputs
    if (gYearInput) gYearInput.value = gY;
    if (gMonthSelect) gMonthSelect.value = gM;
    if (gDayInput) {
      const maxDays = new Date(gY, gM, 0).getDate();
      gDayInput.max = maxDays;
      gDayInput.value = gD;
    }

    // Update Displays
    const weekday = getWeekdayName(gDate);
    if (weekdayDisplayEl) weekdayDisplayEl.textContent = weekday;

    const gregFormatted = formatFullGregorian(gD, gM, gY, weekday);
    const hijriFormatted = formatFullHijri(hD, hM, hY, weekday);

    if (gregorianFullTextEl) gregorianFullTextEl.textContent = gregFormatted;
    if (hijriFullTextEl) hijriFullTextEl.textContent = hijriFormatted;
    if (triggerValue) {
      const mName = isAr ? AR_MONTHS_GREGORIAN[gM - 1] : EN_MONTHS_GREGORIAN[gM - 1];
      triggerValue.textContent = `${gD} ${mName} ${gY}`;
    }

    // Update Hijri Meta note
    if (hijriMetaDescEl) {
      const notes = isAr ? HIJRI_MONTH_NOTES_AR : HIJRI_MONTH_NOTES_EN;
      hijriMetaDescEl.textContent = notes[hM] || (isAr ? 'تقويم أم القرى الرسمي المعتمد' : 'Official Umm al-Qura Calendar');
    }

    calendarView = new Date(gY, gM - 1, 1);
    if (!isInitialLoad) {
      if (completedDebounceTimer) clearTimeout(completedDebounceTimer);
      completedDebounceTimer = setTimeout(() => {
        trackEvent('tool_completed', 'hijri-gregorian-converter');
      }, 500);
    }
  } finally {
    isSyncing = false;
  }
}

// 4. Popover Calendar Logic (Matching age-calculator)
function populateCalendarSelects() {
  if (!calSelectMonth || !calSelectYear) return;

  calSelectMonth.innerHTML = (isAr ? AR_MONTHS_GREGORIAN : EN_MONTHS_GREGORIAN).map((name, i) =>
    `<option value="${i}">${name}</option>`
  ).join('');

  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let y = currentYear - 100; y <= currentYear + 50; y++) {
    yearOptions.push(`<option value="${y}">${y}</option>`);
  }
  calSelectYear.innerHTML = yearOptions.join('');
}

function renderCalendar() {
  if (!calWeekdays || !calDays) return;

  populateCalendarSelects();

  const currentMonth = calendarView.getMonth();
  const currentYear = calendarView.getFullYear();

  if (calSelectMonth) calSelectMonth.value = String(currentMonth);
  if (calSelectYear) calSelectYear.value = String(currentYear);

  calWeekdays.replaceChildren();
  calDays.replaceChildren();

  // Weekdays
  for (let weekday = 0; weekday < 7; weekday++) {
    const date = new Date(2023, 0, 1 + weekday);
    const label = document.createElement('span');
    label.textContent = weekdayFormatter.format(date);
    label.title = fullWeekdayFormatter.format(date);
    calWeekdays.append(label);
  }

  // Days grid
  const firstDay = new Date(currentYear, currentMonth, 1);
  const leadingDays = firstDay.getDay();
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

  const selectedY = parseInt(gYearInput?.value, 10);
  const selectedM = parseInt(gMonthSelect?.value, 10) - 1;
  const selectedD = parseInt(gDayInput?.value, 10);

  const today = new Date();
  const isCurrentMonthToday = today.getFullYear() === currentYear && today.getMonth() === currentMonth;
  const isCurrentMonthSelected = selectedY === currentYear && selectedM === currentMonth;

  for (let i = 0; i < leadingDays; i++) {
    const emptyCell = document.createElement('span');
    emptyCell.className = 'age-calendar-empty';
    emptyCell.setAttribute('aria-hidden', 'true');
    calDays.append(emptyCell);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const dayBtn = document.createElement('button');
    dayBtn.type = 'button';
    dayBtn.className = 'age-calendar-day';
    dayBtn.textContent = String(day);
    dayBtn.dataset.day = String(day);

    if (isCurrentMonthSelected && day === selectedD) {
      dayBtn.classList.add('is-selected');
    }
    if (isCurrentMonthToday && day === today.getDate()) {
      dayBtn.classList.add('is-today');
    }

    dayBtn.addEventListener('click', () => {
      if (gYearInput) gYearInput.value = currentYear;
      if (gMonthSelect) gMonthSelect.value = currentMonth + 1;
      if (gDayInput) gDayInput.value = day;
      onGregorianChanged();
      setCalendarOpen(false);
      dateTrigger?.focus();
    });

    calDays.append(dayBtn);
  }
}

function setCalendarOpen(open) {
  if (!calendarPopover || !dateTrigger) return;
  calendarPopover.hidden = !open;
  dateTrigger.setAttribute('aria-expanded', String(open));
  if (open) {
    const y = parseInt(gYearInput?.value, 10) || new Date().getFullYear();
    const m = (parseInt(gMonthSelect?.value, 10) || 1) - 1;
    calendarView = new Date(y, m, 1);
    renderCalendar();
  }
}

// 5. Setup Action Listeners
function setupListeners() {
  // Gregorian input listeners
  [gDayInput, gMonthSelect, gYearInput].forEach((el) => {
    el?.addEventListener('input', onGregorianChanged);
    el?.addEventListener('change', onGregorianChanged);
  });

  // Hijri input listeners
  [hDayInput, hMonthSelect, hYearInput].forEach((el) => {
    el?.addEventListener('input', onHijriChanged);
    el?.addEventListener('change', onHijriChanged);
  });

  // Date Trigger & Popover
  dateTrigger?.addEventListener('click', () => {
    setCalendarOpen(calendarPopover.hidden);
  });

  document.addEventListener('click', (e) => {
    if (calendarPopover && !calendarPopover.hidden && !dateTrigger.contains(e.target) && !calendarPopover.contains(e.target)) {
      setCalendarOpen(false);
    }
  });

  calPrevBtn?.addEventListener('click', () => {
    calendarView = new Date(calendarView.getFullYear(), calendarView.getMonth() - 1, 1);
    renderCalendar();
  });

  calNextBtn?.addEventListener('click', () => {
    calendarView = new Date(calendarView.getFullYear(), calendarView.getMonth() + 1, 1);
    renderCalendar();
  });

  calSelectMonth?.addEventListener('change', () => {
    calendarView = new Date(calendarView.getFullYear(), Number(calSelectMonth.value), 1);
    renderCalendar();
  });

  calSelectYear?.addEventListener('change', () => {
    calendarView = new Date(Number(calSelectYear.value), calendarView.getMonth(), 1);
    renderCalendar();
  });

  calTodayBtn?.addEventListener('click', () => {
    const now = new Date();
    if (gYearInput) gYearInput.value = now.getFullYear();
    if (gMonthSelect) gMonthSelect.value = now.getMonth() + 1;
    if (gDayInput) gDayInput.value = now.getDate();
    onGregorianChanged();
    setCalendarOpen(false);
  });

  calThisMonthBtn?.addEventListener('click', () => {
    const now = new Date();
    calendarView = new Date(now.getFullYear(), now.getMonth(), 1);
    renderCalendar();
  });

  // Button: Set to Today
  btnSetToday?.addEventListener('click', () => {
    const now = new Date();
    if (gYearInput) gYearInput.value = now.getFullYear();
    if (gMonthSelect) gMonthSelect.value = now.getMonth() + 1;
    if (gDayInput) gDayInput.value = now.getDate();
    onGregorianChanged();
    trackEvent('tool_interacted', 'hijri-gregorian-converter', { action: 'set_today' });
  });

  // Copy Gregorian
  copyGregBtn?.addEventListener('click', async () => {
    const text = gregorianFullTextEl?.textContent;
    if (!text || text === '--') return;
    try {
      await copyText(text);
      const original = copyGregBtn.innerHTML;
      copyGregBtn.innerHTML = `
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>
        <span>${isAr ? 'تم النسخ!' : 'Copied!'}</span>
      `;
      setTimeout(() => { copyGregBtn.innerHTML = original; }, 2000);
      trackEvent('tool_interacted', 'hijri-gregorian-converter', { action: 'copy_gregorian' });
    } catch (err) {
      console.warn('Copy failed', err);
    }
  });

  // Copy Hijri
  copyHijriBtn?.addEventListener('click', async () => {
    const text = hijriFullTextEl?.textContent;
    if (!text || text === '--') return;
    try {
      await copyText(text);
      const original = copyHijriBtn.innerHTML;
      copyHijriBtn.innerHTML = `
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>
        <span>${isAr ? 'تم النسخ!' : 'Copied!'}</span>
      `;
      setTimeout(() => { copyHijriBtn.innerHTML = original; }, 2000);
      trackEvent('tool_interacted', 'hijri-gregorian-converter', { action: 'copy_hijri' });
    } catch (err) {
      console.warn('Copy failed', err);
    }
  });

  // Copy Both Dates
  btnCopyBoth?.addEventListener('click', async () => {
    const gText = gregorianFullTextEl?.textContent;
    const hText = hijriFullTextEl?.textContent;
    if (!gText || !hText || gText === '--') return;
    const combined = isAr
      ? `التاريخ الميلادي: ${gText}\nالتاريخ الهجري: ${hText}`
      : `Gregorian Date: ${gText}\nHijri Date: ${hText}`;

    try {
      await copyText(combined);
      const original = btnCopyBoth.innerHTML;
      btnCopyBoth.innerHTML = `
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>
        <span>${isAr ? 'تم نسخ التاريخين!' : 'Both Copied!'}</span>
      `;
      setTimeout(() => { btnCopyBoth.innerHTML = original; }, 2000);
      trackEvent('tool_interacted', 'hijri-gregorian-converter', { action: 'copy_both' });
    } catch (err) {
      console.warn('Copy both failed', err);
    }
  });
}

// 6. Initialization
function init() {
  // Populate Gregorian months
  if (gMonthSelect) {
    gMonthSelect.innerHTML = (isAr ? AR_MONTHS_GREGORIAN : EN_MONTHS_GREGORIAN).map((m, i) =>
      `<option value="${i + 1}">${i + 1} - ${m}</option>`
    ).join('');
  }

  // Populate Hijri months
  if (hMonthSelect) {
    hMonthSelect.innerHTML = (isAr ? AR_MONTHS_HIJRI : EN_MONTHS_HIJRI).map((m, i) =>
      `<option value="${i + 1}">${i + 1} - ${m}</option>`
    ).join('');
  }

  // Set today's date
  const now = new Date();
  if (gYearInput) gYearInput.value = now.getFullYear();
  if (gMonthSelect) gMonthSelect.value = now.getMonth() + 1;
  if (gDayInput) gDayInput.value = now.getDate();

  setupListeners();
  onGregorianChanged();
  trackEvent('tool_started', 'hijri-gregorian-converter');
  setTimeout(() => { isInitialLoad = false; }, 100);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
